// Fetch client. The access token lives in this module (memory only) so
// non-React code (the WS hook) can read it too; AuthContext keeps it in
// sync. The refresh token is an httpOnly cookie the browser sends
// automatically — on a 401 we transparently refresh once and retry.

import type { LoginResponse } from '@/types';

let accessToken: string | null = null;

// Listeners let AuthContext react to a silent refresh / forced logout
// that happens deep inside the client (outside React's call stack).
let onRefreshed: ((res: LoginResponse) => void) | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function registerAuthHandlers(handlers: {
  onRefreshed: (res: LoginResponse) => void;
  onAuthLost: () => void;
}) {
  onRefreshed = handlers.onRefreshed;
  onAuthLost = handlers.onAuthLost;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;
  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  // internal: prevents infinite refresh recursion
  _retried?: boolean;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown;
  let message = `${res.status} ${res.statusText}`;
  let code: string | undefined;
  try {
    body = await res.json();
    const b = body as { message?: string; detail?: string; error_code?: string };
    code = b.error_code;
    message = b.message ?? b.detail ?? message;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, message, code, body);
}

// A single in-flight refresh shared by concurrent 401s.
let refreshInflight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  if (!refreshInflight) {
    refreshInflight = (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) return false;
        const data = (await res.json()) as LoginResponse;
        accessToken = data.access_token;
        onRefreshed?.(data);
        return true;
      } catch {
        return false;
      } finally {
        refreshInflight = null;
      }
    })();
  }
  return refreshInflight;
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !opts._retried && !path.startsWith('/api/auth/')) {
    const ok = await doRefresh();
    if (ok) return api<T>(path, { ...opts, _retried: true });
    onAuthLost?.();
    throw await parseError(res);
  }

  if (!res.ok) throw await parseError(res);

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
